import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IntercomEvents } from "../../../../shared/enum/intercom-events";
import { getIsRequestSucceeded } from "../../../../shared/utils/get-request-status";
import { RequestStatus } from "../../../../shared/enum/request-status";
import AiCreditsLoader from "./ai-credits-loader";
import { OverlayPopover } from "../../../../shared/components/overlay/popover";
import { Placement, Theme } from "../../../../shared/components/overlay/enums";
import { aiCreditsDownloadUsageReport } from "../../extra-actions";
import { DateTime } from "luxon";
import { RootState } from "../../../../store/root-reducer";
import {
  getUserRole,
  getUserTimeZone,
} from "../../../../shared/utils/user-details";
import DateFilter from "../../../../shared/components/date-filter/date-filter";
import { executeOnRequestStatus } from "../../../../shared/utils";
import { Download } from "@saleshandy/icons";
import { resetAiCreditsDownloadUsageReport } from "../../billing-slice";
import { Button } from "@saleshandy/design-system";
import toaster from "@saleshandy/designs/lib/toaster";
import { AgencyRole } from "../../../../shared/enum";
import Stars from "../../../../shared/svg/stars";

interface AiCreditsProps {
  availableCredits: number;
}

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.1999 5.66325L6.42692 10.4633L4.79993 8.82707"
      stroke="#202020"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const AiCreditsDetails: React.FC<AiCreditsProps> = ({ availableCredits }) => {
  const [isHelpscoutBeaconOpen, setIsHelpscoutBeaconOpen] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const timeZone = getUserTimeZone();
  const dispatch = useDispatch();
  const aiCreditsDownloadUsageReportStatus = useSelector(
    (state: RootState) => state.billing.aiCreditsDownloadUsageReport
  );

  const getAccountPlanUsageRequestStatus = useSelector(
    (state: RootState) => state.billing.getAccountPlanUsageRequest.status
  );

  const disableExport =
    aiCreditsDownloadUsageReportStatus?.status === RequestStatus.Pending;

  const role = getUserRole();

  const intercomHelperFunction = () => {
    if (window?.Intercom && !isHelpscoutBeaconOpen) {
      window?.Intercom(IntercomEvents.SHOW, IntercomEvents.ON_SHOW, () => {
        setIsHelpscoutBeaconOpen(true);
      });
    }
    if (window?.Intercom && isHelpscoutBeaconOpen) {
      window?.Intercom(IntercomEvents.HIDE, IntercomEvents.ON_HIDE, () => {
        setIsHelpscoutBeaconOpen(false);
      });
    }
  };

  const onIntercomScoutToggle = () => {
    setIsHelpscoutBeaconOpen(!isHelpscoutBeaconOpen);
    intercomHelperFunction();
  };

  if (window?.Intercom) {
    window?.Intercom(IntercomEvents.ON_SHOW, () => {
      setIsHelpscoutBeaconOpen(true);
    });
    window?.Intercom(IntercomEvents.ON_HIDE, () => {
      setIsHelpscoutBeaconOpen(false);
    });
  }

  const overlayRef = useRef(null);

  const handleClickOutside = (event) => {
    const { target } = event;

    if (overlayRef?.current?.contains(target)) return;

    const popoverEle = document.querySelector(".download-usage-popover");
    if (popoverEle?.contains(target)) return;

    const dateFilterDropdownEle = document.querySelector(".date-filter-select");
    if (dateFilterDropdownEle?.contains(target)) {
      return;
    }

    setShowPopover(false);
  };

  const handleShowPopover = (value: boolean) => {
    setShowPopover(value);
  };

  const [selectedDateRange, setSelectedDateRange] = useState({
    selectedDateRangeOption: "today",
    startDate: DateTime.now().set({
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0,
    }),
    endDate: DateTime.now().set({
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 999,
    }),
    displayText: "Today",
  });

  const handleDateTimeSelectionChange = (
    label,
    startDate,
    endDate,
    displayText
  ) => {
    const now = DateTime.now().setZone(timeZone);

    setSelectedDateRange({
      selectedDateRangeOption: label,
      startDate: (startDate || now).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      }),
      endDate: (endDate || now).set({
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999,
      }),
      displayText,
    });
  };

  const handleExport = (selectedDateRange) => {
    const payload = {
      startDate: selectedDateRange?.startDate || "",
      endDate: selectedDateRange?.endDate || "",
    };
    dispatch(aiCreditsDownloadUsageReport(payload));
  };

  useEffect(() => {
    executeOnRequestStatus({
      status: aiCreditsDownloadUsageReportStatus?.status,
      onSuccess: () => {
        setShowPopover(false);
        toaster.success(aiCreditsDownloadUsageReportStatus?.message, {
          theme: Theme.New,
        });
        dispatch(resetAiCreditsDownloadUsageReport());
      },
    });
  }, [aiCreditsDownloadUsageReportStatus?.status]);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => { 
    if (getIsRequestSucceeded(getAccountPlanUsageRequestStatus)) {
      setIsLoading(false);
    }
  }, [getAccountPlanUsageRequestStatus]);

  return (
    <div className="ai-credits-container">
      {isLoading ? (
        <AiCreditsLoader />
      ) : (
        <div className="ai-credits-card">
          <div className="ai-credits-card-header">
            <div className="d-flex align-items-center">
              <div className="d-flex align-items-center">
                <Stars className="stars-icon" />
              </div>
              <span className="ai-credits-card-title">AI Credits</span>
            </div>
            <div className="d-flex align-items-center">
              <div className="usage-popover" ref={overlayRef}>
                {(role === AgencyRole.AgencyOwner ||
                  role === AgencyRole.AgencyAdmin) && (
                  <OverlayPopover
                    content={
                      <div className="download-usage-popover-content">
                        <div className="download-usage-time-range">
                          <span className="time-range-title">
                            Select time period
                          </span>
                          <DateFilter
                            selectedDateRangeOption={
                              selectedDateRange.selectedDateRangeOption
                            }
                            startDate={selectedDateRange.startDate}
                            endDate={selectedDateRange.endDate}
                            displayText={selectedDateRange.displayText}
                            onDateTimeSelectionChange={
                              handleDateTimeSelectionChange
                            }
                            shouldHaveMinMaxValueLimit={false}
                            shouldHaveMaxValueLimit={true}
                            shouldHaveOneYearLimit={true}
                            shouldShowDateTooltip={true}
                          />
                        </div>
                        <div className="download-usage-action">
                          <Button
                            className="export-btn"
                            isLoading={disableExport}
                            loadingText="Exporting..."
                            disabled={disableExport}
                            onClick={() => handleExport(selectedDateRange)}
                          >
                            Export
                          </Button>
                        </div>
                      </div>
                    }
                    placement={Placement.BottomEnd}
                    show={showPopover}
                    onToggle={handleShowPopover}
                    className="download-usage-popover"
                  >
                    <span className="download-usage">
                      <Download />
                      <span className="download-txt">
                        Download Usage Report
                      </span>
                    </span>
                  </OverlayPopover>
                )}
              </div>
              {role === AgencyRole.AgencyOwner && (
                <Button
                  onClick={() => {
                    onIntercomScoutToggle();
                  }}
                  className="ml-4"
                >
                  Purchase Credits
                </Button>
              )}
            </div>
          </div>
          <div className="ai-credits-card-body">
            <div className="ai-credits-data">
              <span className="semibold-1">Available Credits</span>
              {availableCredits && (
                <span
                  className={`available-credits ${
                    availableCredits < 100 ? "low-credit" : "high-credit"
                  }`}
                >
                  {availableCredits < 10
                    ? availableCredits == 0
                      ? availableCredits
                      : Number(availableCredits)?.toFixed(2)
                    : Math.ceil(availableCredits)}
                </span>
              )}
            </div>
          </div>
          <hr className="custom-hr" />
          <div className="help-ai-credits">
            <span className="bold-text">Smarter AI Credit. </span>
            <span className="normal-text">
              Along with tracking usage, get insights that help you optimize
              your credit spending like:
            </span>
            <ul className="features-list">
              <li>
                <CheckIcon />
                <span className="feature-text">AI Columns</span>
              </li>
              {/* <li>
                <CheckIcon />
                <span className="feature-text">AI Sequence Generator</span>
              </li> */}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
export default AiCreditsDetails;
